import {
  mockCreateAccount,
  mockCrmDetails,
  mockFilterAccountsList,
  mockFindAccountByCrmId,
  mockFindAccountById,
  mockSearchCrmDirectory,
  mockInviteAccountTeamMember,
  mockRevokeAccountTeamMember,
  mockUpdateAccountOwner,
  MOCK_ACCOUNT_OWNER,
} from './accounts-api-mock.fixtures';
import { mockMomentumUserFromOId } from './users-api-mock.fixtures';

function readUrlSearchParams(url: string): URLSearchParams {
  const queryIndex = url.indexOf('?');
  if (queryIndex < 0) {
    return new URLSearchParams();
  }
  return new URLSearchParams(url.slice(queryIndex + 1));
}

export function mockHandleAccountsRoute(
  method: string,
  canonical: string,
  body: unknown,
  url: string,
): { status: number; body: unknown } | null {
  if (canonical === '/api/accounts/directory' && method === 'GET') {
    const params = readUrlSearchParams(url);
    const search = params.get('search') ?? '';
    const limit = Number(params.get('limit') ?? '20');
    return {
      status: 200,
      body: mockSearchCrmDirectory(search, Number.isFinite(limit) ? limit : 20),
    };
  }

  const crmInfoMatch = /^\/api\/accounts\/crm-account-information\/([^/]+)$/.exec(canonical);
  if (crmInfoMatch && method === 'GET') {
    const crmAccountId = decodeURIComponent(crmInfoMatch[1]);
    const details = mockCrmDetails(crmAccountId);
    return details ? { status: 200, body: details } : { status: 404, body: null };
  }

  if (canonical === '/api/accounts' && method === 'GET') {
    const params = readUrlSearchParams(url);
    const search = params.get('search') ?? '';
    const companyId = params.get('companyId');
    const createdByOid = params.get('createdByOid');
    const pageSize = Math.max(1, Number.parseInt(params.get('pageSize') ?? '25', 10) || 25);
    const continuationToken = params.get('continuationToken');
    const offset = continuationToken ? Number.parseInt(continuationToken, 10) || 0 : 0;
    const filtered = mockFilterAccountsList(search, companyId, createdByOid);
    const pageItems = filtered.slice(offset, offset + pageSize);
    const nextOffset = offset + pageItems.length;
    const hasMore = nextOffset < filtered.length;
    return {
      status: 200,
      body: {
        items: pageItems,
        continuationToken: hasMore ? String(nextOffset) : null,
      },
    };
  }

  if (canonical === '/api/accounts' && method === 'POST') {
    const crmAccountId =
      typeof body === 'object' && body !== null && 'crmAccountId' in body
        ? String((body as { crmAccountId: string }).crmAccountId)
        : '';
    if (!crmAccountId) {
      return {
        status: 400,
        body: { title: 'Cannot create account', detail: 'crmAccountId is required.' },
      };
    }
    const existing = mockFindAccountByCrmId(crmAccountId);
    if (existing) {
      return { status: 409, body: existing };
    }
    try {
      const created = mockCreateAccount(crmAccountId, MOCK_ACCOUNT_OWNER);
      return { status: 201, body: created };
    } catch {
      return {
        status: 400,
        body: { title: 'Cannot create account', detail: 'CRM account was not found.' },
      };
    }
  }

  const ownerMatch = /^\/api\/accounts\/([^/]+)\/owner$/.exec(canonical);
  if (ownerMatch && method === 'POST') {
    const accountId = decodeURIComponent(ownerMatch[1]);
    const userOId =
      typeof body === 'object' && body !== null && 'userOId' in body
        ? String((body as { userOId: string }).userOId ?? '')
        : '';
    if (!userOId.trim()) {
      return {
        status: 400,
        body: { title: 'Cannot reassign account owner', detail: 'userOId is required.' },
      };
    }
    const owner = mockMomentumUserFromOId(userOId.trim()) ?? { oId: userOId.trim() };
    const updated = mockUpdateAccountOwner(accountId, owner);
    return updated ? { status: 200, body: updated } : { status: 404, body: null };
  }

  const teamMatch = /^\/api\/accounts\/([^/]+)\/team$/.exec(canonical);
  if (teamMatch && method === 'POST') {
    const accountId = decodeURIComponent(teamMatch[1]);
    const userOId =
      typeof body === 'object' && body !== null && 'userOId' in body
        ? String((body as { userOId: string }).userOId ?? '')
        : '';
    if (!userOId.trim()) {
      return {
        status: 400,
        body: { title: 'Cannot invite account team member', detail: 'userOId is required.' },
      };
    }
    try {
      const member = mockMomentumUserFromOId(userOId.trim()) ?? { oId: userOId.trim() };
      const updated = mockInviteAccountTeamMember(accountId, member);
      return updated ? { status: 200, body: updated } : { status: 404, body: null };
    } catch (error) {
      return {
        status: 409,
        body: {
          title: 'Cannot invite account team member',
          detail: error instanceof Error ? error.message : 'Conflict.',
        },
      };
    }
  }

  const teamRevokeMatch = /^\/api\/accounts\/([^/]+)\/team\/([^/]+)$/.exec(canonical);
  if (teamRevokeMatch && method === 'DELETE') {
    const accountId = decodeURIComponent(teamRevokeMatch[1]);
    const userOId = decodeURIComponent(teamRevokeMatch[2]);
    const updated = mockRevokeAccountTeamMember(accountId, userOId);
    return updated ? { status: 200, body: updated } : { status: 404, body: null };
  }

  const accountMatch = /^\/api\/accounts\/([^/]+)$/.exec(canonical);
  if (accountMatch) {
    const accountId = decodeURIComponent(accountMatch[1]);
    if (method === 'GET') {
      const account = mockFindAccountById(accountId);
      return account ? { status: 200, body: account } : { status: 404, body: null };
    }
  }

  return null;
}
